import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowLeft, Ban, Layers, RefreshCw, TrendingDown, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import {
    fetchStatus, fetchGroupSellActive, previewGroupSell, executeGroupSell,
    fetchOrphans, resolveOrphansManual, fetchCombos, cancelCombo,
} from "@/lib/api";
import { fmtDur } from "@/components/HistoryPage";

// The GridDesk bot's group-sell API. The SmartGrid bot injects its own bundle
// (same shape, /api/smartgrid/bot/* endpoints) via the `api` prop → context.
const DEFAULT_GS_API = {
    fetchStatus, fetchGroupSellActive, previewGroupSell, executeGroupSell,
    fetchOrphans, resolveOrphansManual, fetchCombos, cancelCombo,
};
const GsApiContext = React.createContext(DEFAULT_GS_API);
const useGsApi = () => React.useContext(GsApiContext);

const fmtDT = (ms) => (ms ? new Date(ms).toLocaleString("en-US", { hour12: false }) : "--");
const inputCls = "bg-[#0B0F14] border border-[#1B2A3A] rounded px-2 py-1.5 text-sm text-[#E6EDF6] font-mono w-full focus:outline-none focus:border-[#2EE6D6]";
const errDetail = (e) => {
    const d = e?.response?.data?.detail;
    if (d && typeof d === "object") return d.message || d.code;
    return d || e.message;
};
const errCode = (e) => {
    const d = e?.response?.data?.detail;
    return d && typeof d === "object" ? d.code : null;
};

/* ---------------- Live tick ---------------- */
const LiveTick = ({ status }) => {
    const price = status?.last_price;
    const prev = useRef(price);
    const [dir, setDir] = useState(0);
    useEffect(() => {
        if (price != null && prev.current != null && price !== prev.current) setDir(price > prev.current ? 1 : -1);
        prev.current = price;
    }, [price]);
    return (
        <div className="flex items-center gap-3" data-testid="groupsell-live-tick">
            <div className="flex items-center gap-1.5">
                {dir >= 0 ? <TrendingUp size={16} color="#2BE38B" /> : <TrendingDown size={16} color="#FF5C6C" />}
                <span className="font-mono text-xl" style={{ color: dir >= 0 ? "#2BE38B" : "#FF5C6C" }} data-testid="groupsell-live-price">
                    {price ? `$${Number(price).toFixed(2)}` : "--"}
                </span>
            </div>
            <div className="text-[10px] font-mono text-[#6F7D92]">
                bid {status?.bid ? Number(status.bid).toFixed(2) : "--"} · ask {status?.ask ? Number(status.ask).toFixed(2) : "--"}
            </div>
            <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.12em] font-mono"
                style={{
                    background: status?.mode === "live" ? "rgba(255,92,108,0.12)" : "rgba(46,230,214,0.12)",
                    color: status?.mode === "live" ? "#FF5C6C" : "#2EE6D6",
                    border: `1px solid ${status?.mode === "live" ? "rgba(255,92,108,0.3)" : "rgba(46,230,214,0.3)"}`,
                }} data-testid="groupsell-mode-badge">
                {status?.mode || "--"}
            </span>
        </div>
    );
};

/* ---------------- Sell builder (shared by active + orphan) ---------------- */
const SellBuilder = ({ source, preview, price, setPrice, stale, onRecalc, onExecuted, lastPrice }) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [ackLoss, setAckLoss] = useState(false);
    const [ackTarget, setAckTarget] = useState(false);
    const [busy, setBusy] = useState(false);
    const A = useGsApi();

    const p = Number(price);
    const validPrice = Number.isFinite(p) && p > 0;
    const belowBuy = validPrice && preview && p < preview.weighted_avg_buy;
    const belowTarget = validPrice && preview && p < preview.avg_sell_target;
    const estPnl = validPrice && preview ? (p - preview.weighted_avg_buy) * preview.combined_qty : null;
    const marketable = validPrice && lastPrice && p <= lastPrice;

    const doExecute = async () => {
        setBusy(true);
        try {
            const res = await A.executeGroupSell({
                source,
                grid_ids: preview.legs.map((l) => l.grid_id),
                price: p,
                selection_hash: preview.selection_hash,
                confirm_loss: ackLoss,
                confirm_below_target: ackTarget || ackLoss,
            });
            toast.success(`Combined SELL placed @ $${p.toFixed(2)} (${res.combo.qty} BTC, ${preview.legs.length} orders)`);
            setConfirmOpen(false);
            setAckLoss(false); setAckTarget(false);
            onExecuted();
        } catch (e) {
            const code = errCode(e);
            toast.error(errDetail(e));
            if (code === "SELECTION_CHANGED") onRecalc(true);
            setConfirmOpen(false);
        } finally {
            setBusy(false);
        }
    };

    if (!preview || preview.legs.length === 0) {
        return (
            <div className="panel p-4 text-center text-[#3A4759] text-xs" data-testid="groupsell-builder-empty">
                Select orders on the left to build a combined SELL.
            </div>
        );
    }

    return (
        <div className="panel p-4 space-y-3" data-testid="groupsell-builder">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6] flex items-center gap-1.5">
                <Layers size={12} /> Combined SELL Builder — {preview.legs.length} order(s)
            </div>

            {stale && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 rounded border text-xs"
                    style={{ background: "rgba(240,185,74,0.10)", borderColor: "rgba(240,185,74,0.4)", color: "#F0B94A" }}
                    data-testid="groupsell-stale-warning">
                    <span className="flex items-center gap-1.5"><AlertTriangle size={13} /> Selected orders changed (a fill/cancel occurred). Recalculate before placing.</span>
                    <button onClick={() => onRecalc(false)} className="btn-anim px-2 py-1 rounded border border-[#F0B94A] hover:bg-[rgba(240,185,74,0.15)]"
                        data-testid="groupsell-recalculate-button">Recalculate</button>
                </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <div className="bg-[#0B0F14] rounded p-2 border border-[#13202D]">
                    <div className="text-[9px] uppercase text-[#6F7D92]">Combined Qty</div>
                    <div data-testid="groupsell-combined-qty">{preview.combined_qty} BTC</div>
                </div>
                <div className="bg-[#0B0F14] rounded p-2 border border-[#13202D]">
                    <div className="text-[9px] uppercase text-[#6F7D92]">Avg Buy Price</div>
                    <div className="text-[#3DA7FF]" data-testid="groupsell-avg-buy">${preview.weighted_avg_buy.toFixed(2)}</div>
                </div>
                <div className="bg-[#0B0F14] rounded p-2 border border-[#13202D]">
                    <div className="text-[9px] uppercase text-[#6F7D92]">Avg Sell Target</div>
                    <div className="text-[#A7B4C6]" data-testid="groupsell-avg-target">${preview.avg_sell_target.toFixed(2)}</div>
                </div>
                <div className="bg-[#0B0F14] rounded p-2 border border-[#13202D]">
                    <div className="text-[9px] uppercase text-[#6F7D92]">Cost Basis</div>
                    <div className="text-[#A7B4C6]">${preview.cost_usdt.toFixed(2)}</div>
                </div>
            </div>

            <div>
                <label className="text-[9px] uppercase tracking-[0.14em] text-[#6F7D92] mb-1 block">Combined Sell Price (USDT)</label>
                <input className={inputCls} type="number" step="0.01" value={price}
                    onChange={(e) => setPrice(e.target.value)} placeholder={`e.g. ${preview.avg_sell_target.toFixed(2)}`}
                    data-testid="groupsell-price-input" />
            </div>

            {validPrice && (
                <div className="space-y-1.5">
                    <div className="text-xs font-mono flex items-center justify-between">
                        <span className="text-[#6F7D92]">Estimated PnL at ${p.toFixed(2)}:</span>
                        <span style={{ color: estPnl >= 0 ? "#2BE38B" : "#FF5C6C" }} data-testid="groupsell-est-pnl">
                            {estPnl >= 0 ? "+" : ""}${estPnl.toFixed(4)}
                        </span>
                    </div>
                    {belowBuy && (
                        <div className="px-3 py-2 rounded border text-[11px] flex items-center gap-1.5"
                            style={{ background: "rgba(255,92,108,0.10)", borderColor: "rgba(255,92,108,0.45)", color: "#FF5C6C" }}
                            data-testid="groupsell-loss-warning">
                            <AlertTriangle size={13} /> Price is BELOW the average buy price (${preview.weighted_avg_buy.toFixed(2)}) — this locks in a LOSS.
                        </div>
                    )}
                    {!belowBuy && belowTarget && (
                        <div className="px-3 py-2 rounded border text-[11px] flex items-center gap-1.5"
                            style={{ background: "rgba(240,185,74,0.10)", borderColor: "rgba(240,185,74,0.4)", color: "#F0B94A" }}
                            data-testid="groupsell-target-warning">
                            <AlertTriangle size={13} /> Price is below the average original sell target (${preview.avg_sell_target.toFixed(2)}) — reduced profit.
                        </div>
                    )}
                    {marketable && (
                        <div className="text-[10px] text-[#3DA7FF]" data-testid="groupsell-marketable-note">
                            ℹ Price ≤ market (${Number(lastPrice).toFixed(2)}) — order will fill immediately.
                        </div>
                    )}
                </div>
            )}

            <Button disabled={!validPrice || stale || busy} onClick={() => { setAckLoss(false); setAckTarget(false); setConfirmOpen(true); }}
                className="btn-anim w-full h-9 text-xs font-semibold"
                style={{ background: belowBuy ? "#FF5C6C" : "#2EE6D6", color: "#0B0F14" }}
                data-testid="groupsell-place-button">
                Place Combined SELL
            </Button>

            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent className="bg-[#0F1620] border-[#1B2A3A] text-[#E6EDF6]" data-testid="groupsell-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>Confirm Combined SELL</DialogTitle>
                        <DialogDescription className="text-[#A7B4C6] font-mono text-xs">
                            SELL {preview.combined_qty} BTC @ ${p ? p.toFixed(2) : "--"} · {preview.legs.length} order(s) merged ·
                            avg buy ${preview.weighted_avg_buy.toFixed(2)} · est PnL{" "}
                            <span style={{ color: estPnl >= 0 ? "#2BE38B" : "#FF5C6C" }}>{estPnl >= 0 ? "+" : ""}${(estPnl || 0).toFixed(4)}</span>
                        </DialogDescription>
                    </DialogHeader>
                    {belowBuy && (
                        <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded border"
                            style={{ background: "rgba(255,92,108,0.08)", borderColor: "rgba(255,92,108,0.4)", color: "#FF5C6C" }}>
                            <input type="checkbox" checked={ackLoss} onChange={(e) => setAckLoss(e.target.checked)} data-testid="groupsell-ack-loss-checkbox" />
                            I understand this SELL is below my average buy price and locks in a realized LOSS.
                        </label>
                    )}
                    {belowTarget && !belowBuy && (
                        <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded border"
                            style={{ background: "rgba(240,185,74,0.08)", borderColor: "rgba(240,185,74,0.4)", color: "#F0B94A" }}>
                            <input type="checkbox" checked={ackTarget} onChange={(e) => setAckTarget(e.target.checked)} data-testid="groupsell-ack-target-checkbox" />
                            I accept selling below the original grid targets (reduced profit).
                        </label>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmOpen(false)}
                            className="border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28]" data-testid="groupsell-confirm-cancel">Cancel</Button>
                        <Button disabled={busy || (belowBuy && !ackLoss) || (belowTarget && !belowBuy && !ackTarget)} onClick={doExecute}
                            style={{ background: belowBuy ? "#FF5C6C" : "#2EE6D6", color: "#0B0F14" }}
                            data-testid="groupsell-confirm-execute">
                            {busy ? "Placing…" : "Confirm & Place"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

/* ---------------- selection + preview hook ---------------- */
function useSelectionPreview(source, selected, setSelected) {
    const [preview, setPreview] = useState(null);
    const [stale, setStale] = useState(false);
    const A = useGsApi();
    const gids = useMemo(() => Array.from(selected), [selected]);

    const recalc = useCallback(async (fromError = false) => {
        if (gids.length === 0) { setPreview(null); setStale(false); return; }
        try {
            const pv = await A.previewGroupSell(source, gids);
            setPreview(pv);
            setStale(false);
        } catch (e) {
            if (errCode(e) === "SELECTION_CHANGED") {
                toast.warning("Selection changed — some orders are no longer available. Reselect.");
                setSelected(new Set());
                setPreview(null);
                setStale(false);
            } else if (!fromError) {
                toast.error(errDetail(e));
            }
        }
    }, [A, source, gids, setSelected]);

    useEffect(() => { recalc(); }, [recalc]);

    // watch for mid-process fills: poll and compare hashes
    useEffect(() => {
        if (gids.length === 0) return undefined;
        const id = setInterval(async () => {
            try {
                const pv = await A.previewGroupSell(source, gids);
                setPreview((cur) => {
                    if (cur && pv.selection_hash !== cur.selection_hash) setStale(true);
                    return cur ? { ...pv, selection_hash: cur.selection_hash } : pv;
                });
            } catch (e) {
                if (errCode(e) === "SELECTION_CHANGED") setStale(true);
            }
        }, 3000);
        return () => clearInterval(id);
    }, [A, source, gids]);

    return { preview, stale, recalcPreview: recalc };
}

/* ---------------- Active sells tab ---------------- */
const ActiveSellsTab = ({ lastPrice, onExecuted }) => {
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [price, setPrice] = useState("");
    const A = useGsApi();
    const { preview, stale, recalcPreview } = useSelectionPreview("active", selected, setSelected);

    const load = useCallback(async () => {
        try { const r = await A.fetchGroupSellActive(); setRows(r.items || []); } catch (e) { /* transient poll error */ }
    }, [A]);
    useEffect(() => { load(); const id = setInterval(load, 2000); return () => clearInterval(id); }, [load]);

    const toggle = (gid) => setSelected((s) => { const n = new Set(s); n.has(gid) ? n.delete(gid) : n.add(gid); return n; });
    const allSelectable = rows.filter((r) => r.status === "OPEN" && !r.partially_filled);
    const toggleAll = () => setSelected((s) => (s.size === allSelectable.length ? new Set() : new Set(allSelectable.map((r) => r.grid_id))));

    return (
        <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-7 panel" data-testid="groupsell-active-panel">
                <div className="px-4 py-3 border-b border-[#1B2A3A] flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">Active SELL Orders ({rows.length})</div>
                    <button onClick={toggleAll} className="btn-anim text-[10px] text-[#2EE6D6] hover:underline" data-testid="groupsell-select-all-button">
                        {selected.size === allSelectable.length && allSelectable.length > 0 ? "Deselect all" : "Select all"}
                    </button>
                </div>
                <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0" style={{ background: "#0F1620" }}>
                            <tr className="text-left text-[#6F7D92] uppercase tracking-[0.12em] text-[10px]">
                                <th className="px-3 py-2 w-8"></th>
                                <th className="px-3 py-2">Grid</th>
                                <th className="px-3 py-2 text-right">Sell Price</th>
                                <th className="px-3 py-2 text-right">Qty</th>
                                <th className="px-3 py-2 text-right">Buy Price</th>
                                <th className="px-3 py-2 text-right">Vs Market</th>
                                <th className="px-3 py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-[#3A4759]">No active SELL orders.</td></tr>}
                            {rows.map((r) => {
                                const dist = lastPrice ? ((r.price - lastPrice) / lastPrice) * 100 : null;
                                const selectable = r.status === "OPEN" && !r.partially_filled;
                                return (
                                    <tr key={r.grid_id} className={`border-t border-[#13202D] hover:bg-[#121C28] ${selected.has(r.grid_id) ? "bg-[rgba(46,230,214,0.05)]" : ""}`}
                                        data-testid={`groupsell-active-row-${r.grid_id}`}>
                                        <td className="px-3 py-2">
                                            <input type="checkbox" disabled={!selectable} checked={selected.has(r.grid_id)} onChange={() => toggle(r.grid_id)}
                                                data-testid={`groupsell-select-${r.grid_id}`} />
                                        </td>
                                        <td className="px-3 py-2 font-mono text-[#2EE6D6]">{r.grid_id.slice(0, 8)}</td>
                                        <td className="px-3 py-2 text-right font-mono">${Number(r.price).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">{r.qty}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[#3DA7FF]">${Number(r.buy_price).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right font-mono" style={{ color: dist != null && dist <= 0 ? "#2BE38B" : "#A7B4C6" }}>
                                            {dist != null ? `${dist >= 0 ? "+" : ""}${dist.toFixed(2)}%` : "--"}
                                        </td>
                                        <td className="px-3 py-2 font-mono text-[10px] text-[#A7B4C6]">
                                            {r.status}{r.partially_filled ? " · PARTIAL" : ""}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="col-span-12 lg:col-span-5">
                <SellBuilder source="active" preview={preview} price={price} setPrice={setPrice}
                    stale={stale} onRecalc={recalcPreview} lastPrice={lastPrice}
                    onExecuted={() => { setSelected(new Set()); setPrice(""); load(); onExecuted(); }} />
            </div>
        </div>
    );
};

/* ---------------- Orphans tab ---------------- */
const OrphansTab = ({ lastPrice, onExecuted }) => {
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [price, setPrice] = useState("");
    const [manualOpen, setManualOpen] = useState(false);
    const [manualPrice, setManualPrice] = useState("");
    const [busy, setBusy] = useState(false);
    const A = useGsApi();
    const { preview, stale, recalcPreview } = useSelectionPreview("orphan", selected, setSelected);

    const load = useCallback(async () => {
        try { setData(await A.fetchOrphans()); } catch (e) { /* transient poll error */ }
    }, [A]);
    useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

    const rows = data?.items || [];
    const toggle = (gid) => setSelected((s) => { const n = new Set(s); n.has(gid) ? n.delete(gid) : n.add(gid); return n; });

    const selRows = rows.filter((r) => selected.has(r.grid_id));
    const manualQty = selRows.reduce((a, r) => a + r.qty, 0);
    const manualAvgBuy = manualQty > 0 ? selRows.reduce((a, r) => a + r.avg_buy_price * r.qty, 0) / manualQty : 0;
    const mp = Number(manualPrice);
    const manualPnl = Number.isFinite(mp) && mp > 0 ? (mp - manualAvgBuy) * manualQty : null;

    const doManual = async () => {
        setBusy(true);
        try {
            const res = await A.resolveOrphansManual(Array.from(selected), mp);
            toast.success(`Resolved ${res.resolved.length} orphan(s) · PnL adjusted by ${res.pnl_adjustment >= 0 ? "+" : ""}$${res.pnl_adjustment.toFixed(4)}`);
            setManualOpen(false); setManualPrice(""); setSelected(new Set());
            load(); onExecuted();
        } catch (e) {
            toast.error(errDetail(e));
            if (errCode(e) === "SELECTION_CHANGED") { setSelected(new Set()); load(); }
        } finally { setBusy(false); }
    };

    return (
        <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-7 panel" data-testid="orphans-panel">
                <div className="px-4 py-3 border-b border-[#1B2A3A] flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">
                        Orphan Positions ({rows.length}) — bought, but no SELL on the book
                    </div>
                    <div className="text-[9px] font-mono text-[#6F7D92]" data-testid="orphans-exchange-checked">
                        {data?.exchange_checked ? "✓ exchange cross-checked" : data?.mode === "live" ? "exchange check unavailable" : "DB reconciliation (paper)"}
                    </div>
                </div>
                {data?.btc_free != null && (
                    <div className="px-4 py-2 border-b border-[#13202D] flex flex-wrap items-center gap-x-5 gap-y-1 text-[10px] font-mono"
                        data-testid="orphans-wallet-reconciliation">
                        <span className="text-[#6F7D92]">WALLET FREE <span className="text-[#2EE6D6]" data-testid="orphans-btc-free">{Number(data.btc_free).toFixed(8)} BTC</span></span>
                        <span className="text-[#6F7D92]">ORPHANS <span className="text-[#E6EDF6]" data-testid="orphans-effective-total">{Number(data.effective_total_qty ?? rows.reduce((a, r) => a + r.qty, 0)).toFixed(8)} BTC</span></span>
                        {Number(data.unattributed_qty || 0) > 0 && (
                            <span className="text-[#F0B94A]" title="BTC that left the wallet without a per-grid record (manual/external sells, BTC-paid fees). Shaved from the oldest inventory so everything listed is actually sellable."
                                data-testid="orphans-unattributed-drag">
                                −{Number(data.unattributed_qty).toFixed(8)} external/fee drag
                            </span>
                        )}
                    </div>
                )}
                <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0" style={{ background: "#0F1620" }}>
                            <tr className="text-left text-[#6F7D92] uppercase tracking-[0.12em] text-[10px]">
                                <th className="px-3 py-2 w-8"></th>
                                <th className="px-3 py-2">Grid</th>
                                <th className="px-3 py-2 text-right">Qty</th>
                                <th className="px-3 py-2 text-right">Avg Buy</th>
                                <th className="px-3 py-2 text-right">Cost</th>
                                <th className="px-3 py-2 text-right">Unrealized</th>
                                <th className="px-3 py-2 text-right">Held For</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-[#3A4759]">No orphan positions detected. ✓</td></tr>}
                            {rows.map((r) => {
                                const unreal = lastPrice ? (lastPrice - r.avg_buy_price) * r.qty : null;
                                return (
                                    <tr key={r.grid_id} className={`border-t border-[#13202D] hover:bg-[#121C28] ${selected.has(r.grid_id) ? "bg-[rgba(240,185,74,0.05)]" : ""}`}
                                        data-testid={`orphan-row-${r.grid_id}`}>
                                        <td className="px-3 py-2">
                                            <input type="checkbox" checked={selected.has(r.grid_id)} onChange={() => toggle(r.grid_id)}
                                                data-testid={`orphan-select-${r.grid_id}`} />
                                        </td>
                                        <td className="px-3 py-2 font-mono text-[#F0B94A]">{r.grid_id.slice(0, 8)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">{r.qty}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[#3DA7FF]">${Number(r.avg_buy_price).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[#6F7D92]">${Number(r.cost_usdt).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right font-mono" style={{ color: unreal == null ? "#6F7D92" : unreal >= 0 ? "#2BE38B" : "#FF5C6C" }}>
                                            {unreal != null ? `${unreal >= 0 ? "+" : ""}$${unreal.toFixed(4)}` : "--"}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-[#6F7D92]">{fmtDur(r.age_ms)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {selected.size > 0 && (
                    <div className="px-4 py-3 border-t border-[#1B2A3A] flex items-center justify-between">
                        <div className="text-[10px] font-mono text-[#A7B4C6]">{selected.size} selected · {manualQty.toFixed(8)} BTC</div>
                        <Button variant="outline" onClick={() => setManualOpen(true)}
                            className="btn-anim h-7 text-[10px] border-[#F0B94A] bg-transparent text-[#F0B94A] hover:bg-[rgba(240,185,74,0.1)]"
                            data-testid="orphan-manual-resolve-button">
                            Mark Sold Manually
                        </Button>
                    </div>
                )}
            </div>
            <div className="col-span-12 lg:col-span-5">
                <SellBuilder source="orphan" preview={preview} price={price} setPrice={setPrice}
                    stale={stale} onRecalc={recalcPreview} lastPrice={lastPrice}
                    onExecuted={() => { setSelected(new Set()); setPrice(""); load(); onExecuted(); }} />
            </div>

            <Dialog open={manualOpen} onOpenChange={setManualOpen}>
                <DialogContent className="bg-[#0F1620] border-[#1B2A3A] text-[#E6EDF6]" data-testid="orphan-manual-dialog">
                    <DialogHeader>
                        <DialogTitle>Mark Sold Manually</DialogTitle>
                        <DialogDescription className="text-[#A7B4C6] text-xs">
                            You sold this inventory outside the bot. Enter the price you sold at — realized PnL will be adjusted automatically.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 text-xs font-mono">
                        <div className="text-[#A7B4C6]">{selected.size} orphan(s) · {manualQty.toFixed(8)} BTC · avg buy ${manualAvgBuy.toFixed(2)}</div>
                        <input className={inputCls} type="number" step="0.01" placeholder="actual sell price" value={manualPrice}
                            onChange={(e) => setManualPrice(e.target.value)} data-testid="orphan-manual-price-input" />
                        {manualPnl != null && (
                            <div>PnL adjustment: <span style={{ color: manualPnl >= 0 ? "#2BE38B" : "#FF5C6C" }} data-testid="orphan-manual-pnl">
                                {manualPnl >= 0 ? "+" : ""}${manualPnl.toFixed(4)}</span></div>
                        )}
                        {manualPnl != null && manualPnl < 0 && (
                            <div className="px-2 py-1.5 rounded border text-[10px]" style={{ background: "rgba(255,92,108,0.08)", borderColor: "rgba(255,92,108,0.4)", color: "#FF5C6C" }}>
                                ⚠ This records a realized LOSS.
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setManualOpen(false)}
                            className="border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28]">Cancel</Button>
                        <Button disabled={busy || !(mp > 0)} onClick={doManual} style={{ background: "#F0B94A", color: "#0B0F14" }}
                            data-testid="orphan-manual-confirm-button">
                            {busy ? "Saving…" : "Record Sale"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

/* ---------------- Combos tab ---------------- */
const CombosTab = ({ refreshKey }) => {
    const [items, setItems] = useState([]);
    const A = useGsApi();
    const load = useCallback(async () => {
        try { const r = await A.fetchCombos(); setItems(r.items || []); } catch (e) { /* transient poll error */ }
    }, [A]);
    useEffect(() => { load(); const id = setInterval(load, 3000); return () => clearInterval(id); }, [load, refreshKey]);

    const onCancel = async (id) => {
        try {
            const r = await A.cancelCombo(id);
            toast.success(`Combo cancelled — restored ${r.restored} individual SELL(s)`);
            load();
        } catch (e) { toast.error(errDetail(e)); }
    };

    const stColor = { OPEN: "#3DA7FF", PARTIALLY_FILLED: "#F0B94A", FILLED: "#2BE38B", CANCELLED: "#6F7D92", CANCELLED_STALE: "#6F7D92" };
    return (
        <div className="panel" data-testid="combos-panel">
            <div className="px-4 py-3 border-b border-[#1B2A3A] text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">
                Combined Orders ({items.length})
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-[#6F7D92] uppercase tracking-[0.12em] text-[10px]">
                            <th className="px-3 py-2">Created</th>
                            <th className="px-3 py-2">Combo</th>
                            <th className="px-3 py-2">Source</th>
                            <th className="px-3 py-2 text-right">Price</th>
                            <th className="px-3 py-2 text-right">Qty</th>
                            <th className="px-3 py-2 text-right">Avg Buy</th>
                            <th className="px-3 py-2 text-right">Legs</th>
                            <th className="px-3 py-2 text-right">Filled</th>
                            <th className="px-3 py-2 text-right">PnL</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={11} className="px-3 py-6 text-center text-[#3A4759]">No combined orders yet.</td></tr>}
                        {items.map((c) => {
                            const fillPct = c.qty > 0 ? (100 * (c.cum_filled_qty || 0)) / c.qty : 0;
                            const open = ["OPEN", "PARTIALLY_FILLED"].includes(c.status);
                            return (
                                <tr key={c.combo_id} className="border-t border-[#13202D] hover:bg-[#121C28]" data-testid={`combo-row-${c.combo_id}`}>
                                    <td className="px-3 py-2 font-mono text-[#6F7D92]">{fmtDT(c.created_at_ms)}</td>
                                    <td className="px-3 py-2 font-mono text-[#2EE6D6]">{c.combo_id.slice(0, 8)}</td>
                                    <td className="px-3 py-2 font-mono text-[10px] text-[#A7B4C6] uppercase">{c.source}</td>
                                    <td className="px-3 py-2 text-right font-mono">${Number(c.price).toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">{c.qty}</td>
                                    <td className="px-3 py-2 text-right font-mono text-[#3DA7FF]">${Number(c.weighted_avg_buy).toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">{(c.legs || []).length}</td>
                                    <td className="px-3 py-2 text-right font-mono text-[#A7B4C6]">{fillPct.toFixed(0)}%</td>
                                    <td className="px-3 py-2 text-right font-mono" style={{ color: (c.realized_pnl || 0) >= 0 ? "#2BE38B" : "#FF5C6C" }}>
                                        {c.realized_pnl != null ? `${c.realized_pnl >= 0 ? "+" : ""}$${Number(c.realized_pnl).toFixed(4)}` : "--"}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[10px]" style={{ color: stColor[c.status] || "#A7B4C6" }}>{c.status}</td>
                                    <td className="px-3 py-2 text-right">
                                        {open && (
                                            <button onClick={() => onCancel(c.combo_id)}
                                                className="btn-anim inline-flex items-center gap-1 px-2 py-1 rounded border border-[#FF5C6C] text-[#FF5C6C] text-[10px] hover:bg-[rgba(255,92,108,0.1)]"
                                                data-testid={`combo-cancel-${c.combo_id}`}>
                                                <Ban size={10} /> Cancel &amp; Restore
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/* ---------------- Page ---------------- */
export default function GroupSellPage({ onBack, api, title = "Group Sell & Orphan Manager", backLabel = "Dashboard" }) {
    const A = useMemo(() => api || DEFAULT_GS_API, [api]);
    const [tab, setTab] = useState("active");
    const [status, setStatus] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        let alive = true;
        const poll = async () => { try { const s = await A.fetchStatus(); if (alive) setStatus(s); } catch (e) { /* transient poll error */ } };
        poll();
        const id = setInterval(poll, 1000);
        return () => { alive = false; clearInterval(id); };
    }, [A]);

    const tabs = [
        { id: "active", label: "Active Sells" },
        { id: "orphans", label: "Orphans" },
        { id: "combos", label: "Combined Orders" },
    ];

    return (
        <GsApiContext.Provider value={A}>
        <div className="min-h-screen text-[#E6EDF6]" style={{ background: "#0B0F14" }} data-testid="groupsell-page">
            <Toaster position="bottom-right" toastOptions={{ style: { background: "#0F1620", border: "1px solid #1B2A3A", color: "#E6EDF6" } }} />
            <div className="border-b border-[#1B2A3A]" style={{ background: "#0F1620" }}>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <button onClick={onBack} className="btn-anim text-[#A7B4C6] hover:text-[#E6EDF6] flex items-center gap-1.5 text-xs" data-testid="groupsell-back-button">
                            <ArrowLeft size={14} /> {backLabel}
                        </button>
                        <div className="text-sm font-semibold tracking-wide">{title}</div>
                    </div>
                    <LiveTick status={status} />
                </div>
            </div>

            {!status?.running && (
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 pt-3">
                    <div className="px-3 py-2 rounded border text-xs flex items-center gap-1.5"
                        style={{ background: "rgba(240,185,74,0.08)", borderColor: "rgba(240,185,74,0.35)", color: "#F0B94A" }}
                        data-testid="groupsell-bot-stopped-warning">
                        <AlertTriangle size={13} /> Bot is stopped — placing/cancelling combined orders requires the bot to be running. Detection still works.
                    </div>
                </div>
            )}

            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-4 space-y-4">
                <div className="flex items-center gap-1.5" data-testid="groupsell-tabs">
                    {tabs.map((t) => (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            className="btn-anim px-3 py-1.5 rounded text-xs transition-colors"
                            style={tab === t.id
                                ? { background: "rgba(46,230,214,0.14)", border: "1px solid rgba(46,230,214,0.4)", color: "#2EE6D6" }
                                : { border: "1px solid #1B2A3A", color: "#A7B4C6" }}
                            data-testid={`groupsell-tab-${t.id}`}>
                            {t.label}
                        </button>
                    ))}
                    <div className="ml-auto">
                        <button onClick={() => setRefreshKey((k) => k + 1)}
                            className="btn-anim flex items-center gap-1 text-[10px] text-[#6F7D92] hover:text-[#E6EDF6]"
                            data-testid="groupsell-refresh-button">
                            <RefreshCw size={11} /> refresh
                        </button>
                    </div>
                </div>

                {tab === "active" && <ActiveSellsTab lastPrice={status?.last_price} onExecuted={() => setRefreshKey((k) => k + 1)} />}
                {tab === "orphans" && <OrphansTab lastPrice={status?.last_price} onExecuted={() => setRefreshKey((k) => k + 1)} />}
                {tab === "combos" && <CombosTab refreshKey={refreshKey} />}
            </div>
        </div>
        </GsApiContext.Provider>
    );
}
